import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PaginationParams } from '../types/common';
import { CreateTrainingDto, UpdateTrainingDto } from '../types/dtos/training';
import { createTrainingService } from '../services/training.service';

const TRAININGS_KEY = 'trainings';

export const useTrainings = (organizationId: string, params?: PaginationParams) => {
  const trainingService = createTrainingService(organizationId);

  return useQuery({
    queryKey: [TRAININGS_KEY, organizationId, params],
    queryFn: () => trainingService.findAll(params).then((res) => res.data),
    enabled: !!organizationId
  });
};

export const useTraining = (organizationId: string, trainingId: string) => {
  const trainingService = createTrainingService(organizationId);

  return useQuery({
    queryKey: [TRAININGS_KEY, organizationId, trainingId],
    queryFn: () => trainingService.findById(trainingId).then((res) => res.data),
    enabled: !!organizationId && !!trainingId
  });
};

export const useCreateTraining = (organizationId: string) => {
  const queryClient = useQueryClient();
  const trainingService = createTrainingService(organizationId);

  return useMutation({
    mutationFn: (data: CreateTrainingDto) => 
      trainingService.create(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRAININGS_KEY, organizationId] });
    }
  });
};

export const useUpdateTraining = (organizationId: string) => {
  const queryClient = useQueryClient();
  const trainingService = createTrainingService(organizationId);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTrainingDto }) =>
      trainingService.update(id, data).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [TRAININGS_KEY, organizationId] });
      queryClient.invalidateQueries({ queryKey: [TRAININGS_KEY, organizationId, id] });
    }
  });
};

export const useDeleteTraining = (organizationId: string) => {
  const queryClient = useQueryClient();
  const trainingService = createTrainingService(organizationId);

  return useMutation({
    mutationFn: (id: string) => trainingService.delete(id).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRAININGS_KEY, organizationId] });
    }
  });
};

